import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CodeIcon, MapPinIcon, DatabaseIcon, SparklesIcon, UserSearchIcon, MegaphoneIcon, MailIcon, SearchIcon, ArrowRightIcon, ArrowLeftIcon, ChevronDownIcon, SearchCheckIcon, ZapIcon, SendIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { TrustBar } from '../components/TrustBar';
type Category = 'All' | 'Find' | 'Enrich' | 'Outreach';
interface Feature {
  id: string;
  title: string;
  href: string;
  category: Exclude<Category, 'All'>;
  description: string;
  icon: React.ElementType;
  badge?: {
    text: string;
    color: 'blue' | 'orange' | 'green';
  };
}
const features: Feature[] = [{
  id: 'api',
  title: 'Local business API',
  href: '#/local-business-api',
  category: 'Find',
  description: 'The premier local business data API. Search, filter, and access 200M+ local businesses programmatically.',
  icon: CodeIcon
}, {
  id: 'scraper',
  title: 'Google Maps scraper',
  href: '#/google-maps-scraper',
  category: 'Find',
  description: 'Extract business data from Google Maps at scale — contacts, ratings, reviews, hours, and more.',
  icon: MapPinIcon,
  badge: {
    text: 'POPULAR',
    color: 'blue'
  }
}, {
  id: 'database',
  title: 'Local business database',
  href: '#/local-business-database',
  category: 'Find',
  description: 'Download ready-to-use business datasets by category and location. Pre-built datasets updated weekly.',
  icon: DatabaseIcon
}, {
  id: 'enrichment',
  title: 'Local business enrichment',
  href: '#/local-business-enrichment',
  category: 'Enrich',
  description: 'Append missing data points to your existing business lists — emails, phone numbers, social profiles, tech stack.',
  icon: SparklesIcon
}, {
  id: 'owner-finder',
  title: 'Local business owner finder',
  href: '#/owner-finder',
  category: 'Enrich',
  description: 'Find verified decision-maker contacts at any local business. Direct emails, phone numbers, and LinkedIn profiles.',
  icon: UserSearchIcon,
  badge: {
    text: 'HOT',
    color: 'orange'
  }
}, {
  id: 'marketing',
  title: 'Local marketing',
  href: '#/local-marketing',
  category: 'Outreach',
  description: 'Build targeted local business audiences for Meta Ads and direct mail campaigns.',
  icon: MegaphoneIcon
}, {
  id: 'sequencing',
  title: 'Email sequencing',
  href: '#/email-sequencing',
  category: 'Outreach',
  description: 'Create automated multi-step B2B email sequences with built-in personalization and CRM sync.',
  icon: MailIcon
}];
const categories: Category[] = ['All', 'Find', 'Enrich', 'Outreach'];
const workflowTabs = [{
  id: 'find',
  icon: SearchCheckIcon,
  title: '1. Find local businesses',
  description: 'Search our database of 200M+ local businesses or extract data directly from Google Maps to build your initial prospect list.'
}, {
  id: 'enrich',
  icon: ZapIcon,
  title: '2. Enrich with contact data',
  description: 'Append verified owner emails, direct phone numbers, and executive contact details to your business records instantly.'
}, {
  id: 'outreach',
  icon: SendIcon,
  title: '3. Launch outreach campaigns',
  description: 'Use our built-in email sequencing or export custom audiences for Meta and Google Ads to reach decision-makers.'
}];
const faqs = [{
  question: "What's the free trial for Openmart's local business data platform?",
  answer: 'Every new Openmart account gets free credits to explore all features before committing to a paid plan. You can search the database, enrich contacts, and test our APIs — all at no cost. <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" class="text-blue-600 font-semibold hover:underline">Sign up for free →</a>'
}, {
  question: 'How accurate is the local business data?',
  answer: 'We maintain a 95%+ accuracy rate by continuously crawling, verifying, and updating our database of millions of local businesses. Contact data like emails and phone numbers are verified in real-time to ensure your outreach campaigns do not bounce.'
}, {
  question: 'Can I use multiple features together?',
  answer: 'Absolutely. All Openmart features work together seamlessly. For example, you can use the Google Maps Scraper to find businesses, pass them through Local Business Enrichment to find owner emails, and then load them into Email Sequencing for outreach — all within one platform.'
}, {
  question: 'Do you offer an API?',
  answer: 'Yes, our core Local Business API provides programmatic access to our entire 200M+ business database. We also offer specific API endpoints for data enrichment and contact finding, perfect for integrating into your own workflows or CRM.'
}, {
  question: 'What data is included for each business?',
  answer: 'Each business record includes 50+ data fields: company name, address, phone number, website, email, hours of operation, Google rating &amp; reviews, category, social media profiles, tech stack, employee count, revenue estimates, owner/founder name, verified owner email, direct phone number, and LinkedIn profile — all continuously updated and verified.'
}];
export function Features() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [activeWorkflowTab, setActiveWorkflowTab] = useState(workflowTabs[0].id);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const filteredFeatures = useMemo(() => {
    return features.filter(feature => {
      const matchesSearch = feature.title.toLowerCase().includes(searchQuery.toLowerCase()) || feature.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || feature.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);
  const getCategoryColor = (category: Exclude<Category, 'All'>) => {
    switch (category) {
      case 'Find':
        return 'bg-blue-50 text-blue-700';
      case 'Enrich':
        return 'bg-green-50 text-green-700';
      case 'Outreach':
        return 'bg-purple-50 text-purple-700';
    }
  };
  const getBadgeColor = (color: 'blue' | 'orange' | 'green') => {
    switch (color) {
      case 'blue':
        return 'bg-blue-600 text-white';
      case 'orange':
        return 'bg-orange-500 text-white';
      case 'green':
        return 'bg-green-500 text-white';
    }
  };
  return <div className="min-h-screen bg-white font-sans selection:bg-blue-100 selection:text-blue-900 flex flex-col" data-id="element-4245">
      <Navbar data-id="element-4246" />

      <main className="flex-grow" data-id="element-4247">
        {/* Hero Section - Redesigned */}
        <section className="pt-24 pb-16 bg-white" data-id="element-4248">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center" data-id="element-4249">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-4250">
              Explore <span className="text-blue-600" data-id="element-4251">local business</span>{' '}
              features
            </h1>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-4252">
              Find, enrich, and reach 200M+ local businesses. The complete
              platform for local business data.
            </p>

            <div className="flex items-center justify-center gap-3 flex-wrap mt-8" data-id="element-4253">
              <span className="text-sm font-medium text-slate-500" data-id="element-4254">
                <span className="text-blue-600 font-semibold" data-id="element-4255">200M+</span>{' '}
                businesses
              </span>
              <span className="w-1 h-1 rounded-full bg-blue-300 hidden sm:block" data-id="element-4256"></span>
              <span className="text-sm font-medium text-slate-500" data-id="element-4257">
                <span className="text-blue-600 font-semibold" data-id="element-4258">50+</span>{' '}
                countries
              </span>
              <span className="w-1 h-1 rounded-full bg-blue-300 hidden sm:block" data-id="element-4259"></span>
              <span className="text-sm font-medium text-slate-500" data-id="element-4260">
                <span className="text-blue-600 font-semibold" data-id="element-4261">99.5%</span>{' '}
                uptime
              </span>
            </div>

            <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors text-base" data-id="element-4262">
              Try for free <ArrowRightIcon className="w-4 h-4" data-id="element-4263" />
            </a>
          </div>
        </section>

        {/* Search & Filters Section - Distinct from hero */}
        <section className="py-10 bg-slate-50 border-y border-slate-100" data-id="element-4264">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4265">
            <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-4266">
              <div className="relative flex-grow max-w-lg" data-id="element-4267">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-4268">
                  <SearchIcon className="h-5 w-5 text-slate-400" data-id="element-4269" />
                </div>
                <input type="text" className="block w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-shadow text-base" placeholder="Search features..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4270" />
              </div>

              <div className="flex flex-wrap gap-2" data-id="element-4271">
                {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeCategory === category ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`} data-id="element-4272">
                    {category}
                  </button>)}
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-8 pb-20 bg-white" data-id="element-4273">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4274">
            <div className="flex items-center justify-between mb-10" data-id="element-4275">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-4276">
                All features
              </h2>
              <span className="text-sm font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full" data-id="element-4277">
                {filteredFeatures.length}{' '}
                {filteredFeatures.length === 1 ? 'feature' : 'features'}
              </span>
            </div>

            {filteredFeatures.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-4278">
                {filteredFeatures.map(feature => <a key={feature.id} href={feature.href} className="group flex flex-col bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-xl hover:border-slate-300 transition-all duration-300 cursor-pointer h-full" data-id="element-4279">
                    <div className="flex items-start justify-between mb-5" data-id="element-4280">
                      <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors" data-id="element-4281">
                        <feature.icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-4282" />
                      </div>
                      {feature.badge && <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getBadgeColor(feature.badge.color)}`} data-id="element-4283">
                          {feature.badge.text}
                        </span>}
                    </div>

                    <div className="flex-grow" data-id="element-4284">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-4285">
                        {feature.title}
                      </h3>
                      <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold mb-4 ${getCategoryColor(feature.category)}`} data-id="element-4286">
                        {feature.category}
                      </span>
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-3" data-id="element-4287">
                        {feature.description}
                      </p>
                    </div>

                    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center text-sm font-semibold text-blue-600 group-hover:text-blue-700 transition-colors" data-id="element-4288">
                      Learn more{' '}
                      <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-4289" />
                    </div>
                  </a>)}
              </div> : <div className="text-center py-32 bg-slate-50 rounded-3xl border border-slate-100" data-id="element-4290">
                <SearchIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-4291" />
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-4292">
                  No features found
                </h3>
                <p className="text-slate-500" data-id="element-4293">
                  We couldn't find any features matching your search. Try
                  adjusting your filters.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('All');
            }} className="mt-6 px-6 py-2 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4294">
                  Clear filters
                </button>
              </div>}
          </div>
        </section>

        {/* Workflow Section */}
        <section className="py-32 bg-slate-50" data-id="element-4295">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4296">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-4297">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4298">
                The complete{' '}
                <span className="text-blue-600" data-id="element-4299">local business data</span>{' '}
                pipeline
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4300">
                All features work together seamlessly, taking you from raw
                search to booked meetings in one platform.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-4301">
              {/* Left: Tabs */}
              <div className="lg:col-span-5 space-y-2" data-id="element-4302">
                {workflowTabs.map(tab => {
                const isActive = activeWorkflowTab === tab.id;
                return <button key={tab.id} onClick={() => setActiveWorkflowTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-white shadow-sm border border-slate-200' : 'bg-transparent hover:bg-white/50 border border-transparent'}`} data-id="element-4303">
                      <div className="flex items-start gap-6" data-id="element-4304">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-500'}`} data-id="element-4305">
                          <tab.icon className="w-6 h-6" data-id="element-4306" />
                        </div>
                        <div data-id="element-4307">
                          <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-4308">
                            {tab.title}
                          </h3>
                          <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-4309">
                            {tab.description}
                          </p>
                        </div>
                      </div>
                    </button>;
              })}
              </div>

              {/* Right: Visuals */}
              <div className="lg:col-span-7" data-id="element-4310">
                <div className="bg-white rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden border border-slate-200 shadow-sm" data-id="element-4311">
                  <AnimatePresence mode="wait" data-id="element-4312">
                    {activeWorkflowTab === 'find' && <motion.div key="find" initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="w-full max-w-md" data-id="element-4313">
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 mb-4" data-id="element-4314">
                          <div className="flex items-center gap-3 mb-4" data-id="element-4315">
                            <SearchIcon className="w-5 h-5 text-slate-400" data-id="element-4316" />
                            <div className="h-2 w-32 bg-slate-200 rounded-full" data-id="element-4317"></div>
                          </div>
                          <div className="space-y-3" data-id="element-4318">
                            {[1, 2, 3].map(i => <div key={i} className="bg-white p-4 rounded-xl border border-slate-100 flex items-center gap-4" data-id="element-4319">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-4320"></div>
                                <div className="space-y-2 flex-grow" data-id="element-4321">
                                  <div className="h-2 w-24 bg-slate-200 rounded-full" data-id="element-4322"></div>
                                  <div className="h-2 w-16 bg-slate-100 rounded-full" data-id="element-4323"></div>
                                </div>
                              </div>)}
                          </div>
                        </div>
                        <div className="text-center text-sm font-semibold text-blue-600 bg-blue-50 py-2 rounded-xl" data-id="element-4324">
                          200M+ businesses indexed
                        </div>
                      </motion.div>}

                    {activeWorkflowTab === 'enrich' && <motion.div key="enrich" initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="w-full max-w-md" data-id="element-4325">
                        <div className="space-y-4" data-id="element-4326">
                          {[{
                        label: 'Verified emails',
                        value: 'sarah@company.com',
                        color: 'text-green-600',
                        bg: 'bg-green-50'
                      }, {
                        label: 'Direct phones',
                        value: '+1 (415) 555-0192',
                        color: 'text-blue-600',
                        bg: 'bg-blue-50'
                      }, {
                        label: 'Social profiles',
                        value: 'linkedin.com/in/sarah',
                        color: 'text-purple-600',
                        bg: 'bg-purple-50'
                      }].map((item, i) => <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between" data-id="element-4327">
                              <span className="text-sm font-bold text-slate-700" data-id="element-4328">
                                {item.label}
                              </span>
                              <span className={`text-sm font-semibold px-3 py-1 rounded-lg ${item.color} ${item.bg}`} data-id="element-4329">
                                {item.value}
                              </span>
                            </div>)}
                        </div>
                      </motion.div>}

                    {activeWorkflowTab === 'outreach' && <motion.div key="outreach" initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="w-full max-w-md" data-id="element-4330">
                        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm relative" data-id="element-4331">
                          <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm" data-id="element-4332">
                            Sent
                          </div>
                          <div className="border-b border-slate-100 pb-4 mb-4" data-id="element-4333">
                            <div className="text-xs font-bold text-slate-400 uppercase mb-1" data-id="element-4334">
                              Subject
                            </div>
                            <div className="text-sm font-semibold text-slate-900" data-id="element-4335">
                              Quick question about {'{{company}}'}
                            </div>
                          </div>
                          <div className="space-y-2" data-id="element-4336">
                            <div className="h-2 w-3/4 bg-slate-100 rounded-full" data-id="element-4337"></div>
                            <div className="h-2 w-full bg-slate-100 rounded-full" data-id="element-4338"></div>
                            <div className="h-2 w-5/6 bg-slate-100 rounded-full" data-id="element-4339"></div>
                            <div className="h-2 w-1/2 bg-slate-100 rounded-full pt-4" data-id="element-4340"></div>
                          </div>
                          <div className="mt-6 flex gap-2" data-id="element-4341">
                            <div className="flex-1 bg-slate-50 rounded-lg py-2 text-center text-xs font-semibold text-slate-500" data-id="element-4342">
                              <span className="block text-lg text-slate-900 mb-1" data-id="element-4343">
                                45%
                              </span>{' '}
                              Open rate
                            </div>
                            <div className="flex-1 bg-slate-50 rounded-lg py-2 text-center text-xs font-semibold text-slate-500" data-id="element-4344">
                              <span className="block text-lg text-slate-900 mb-1" data-id="element-4345">
                                12%
                              </span>{' '}
                              Reply rate
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 bg-white border-b border-slate-100" data-id="element-4346">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4347">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-4348">
              <div data-id="element-4349">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3" data-id="element-4350">
                  200M+
                </div>
                <div className="text-sm font-semibold text-slate-500 tracking-wider" data-id="element-4351">
                  Local businesses
                </div>
              </div>
              <div data-id="element-4352">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3" data-id="element-4353">
                  50+
                </div>
                <div className="text-sm font-semibold text-slate-500 tracking-wider" data-id="element-4354">
                  Data fields
                </div>
              </div>
              <div data-id="element-4355">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3" data-id="element-4356">
                  95%
                </div>
                <div className="text-sm font-semibold text-slate-500 tracking-wider" data-id="element-4357">
                  Data accuracy
                </div>
              </div>
              <div data-id="element-4358">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3" data-id="element-4359">
                  100%
                </div>
                <div className="text-sm font-semibold text-slate-500 tracking-wider" data-id="element-4360">
                  Real-time verification
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-32 bg-slate-50" data-id="element-4361">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4362">
            <div className="text-center mb-16" data-id="element-4363">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-4364">
                Frequently asked{' '}
                <span className="text-blue-600" data-id="element-4365">questions</span>
              </h2>
            </div>

            <div className="space-y-0 divide-y divide-slate-200" data-id="element-4366">
              {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return <div key={index} className="py-5" data-id="element-4367">
                    <button onClick={() => setOpenFaqIndex(isOpen ? null : index)} className="w-full text-left flex justify-between items-center focus:outline-none group" aria-expanded={isOpen} data-id="element-4368">
                      <span className={`text-base font-semibold transition-colors pr-8 ${isOpen ? 'text-blue-600' : 'text-slate-900 group-hover:text-blue-600'}`} data-id="element-4369">
                        {faq.question}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'transform rotate-180 text-blue-600' : 'text-slate-400 group-hover:text-blue-600'}`} data-id="element-4370" />
                    </button>
                    <AnimatePresence data-id="element-4371">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-4372">
                          <div className="pt-4 pb-2 text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{
                      __html: faq.answer
                    }} data-id="element-4373" />
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-blue-600 relative overflow-hidden" data-id="element-4374">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-4375"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-4376">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-4377">
              Ready to scale your local business outreach?
            </h2>
            <p className="text-xl text-white/80 mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-4378">
              Get free credits when you sign up. Explore all of Openmart's
              features risk-free — no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-4379">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-colors text-lg w-full sm:w-auto justify-center" data-id="element-4380">
                Try for free
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-4381" />
    </div>;
}